import React, { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

//Common
import { LoadingIndicator } from "../../common/VFX-UI";

//App
import { queryKeys } from "./constants";
import { FormikTypeaheadOption } from "../../../types";
import { EMPTY_GUID, GRID_PAGE_SIZE } from "../../../utility/constants/constants";
import { SearchValue } from "../../../store/types";
import {
  CampaignContributionReportContextData,
  CampaignContributionReportState,
} from "../../../types";
import reportApi from "../../../app/model/api/reportApi";
import CampaignContributionReport from "./CampaignContributionReport";

export const CampaignContributionsReportContext =
  React.createContext<CampaignContributionReportContextData | null>(null);

const CampaignsReportsContainer = () => {
  const [state, setState] = useState<CampaignContributionReportState>({
    campaignId: null,
    organizationId: null,
    searchPayload: null,
    pageSize: GRID_PAGE_SIZE,
    currentPage: 1,
  });

  const campaignLookupData = useQuery({
    queryKey: [queryKeys.getCampaignContributionReportLookups],
    queryFn: () => reportApi.getCampaigns(),
    select: (data) =>
      data?.result?.map((dto) => ({
        label: dto.display as string,
        value: dto.id as string,
        organizationId: dto.parentId as string,
      })) as FormikTypeaheadOption[],
  });

  const reportLookups = useQuery({
    queryKey: [queryKeys.getCampaignContributionReportLookups, state.organizationId],
    queryFn: () => reportApi.getReportLookups(state.organizationId as string),
    enabled: state.organizationId != null,
    select: (data) => ({
      contributorTypes: data?.result?.contributorTypes?.map((dto) => ({
        label: dto.display as string,
        value: dto.id as string,
      })) as FormikTypeaheadOption[],
      methodOfGivingTypes: data?.result?.methodOfGivingTypes?.map((dto) => ({
        label: dto.display as string,
        value: dto.id as string,
      })) as FormikTypeaheadOption[],
    }),
  });

  const campaignGridQuery = useQuery({
    queryKey: [
      queryKeys.getCampaignContributionReportData,
      state.campaignId,
      state.searchPayload,
      state.pageSize,
      state.currentPage,
    ],
    queryFn: () =>
      reportApi.reportGetContributionsData({
        ...state.searchPayload,
          campaignId: state.campaignId ?? EMPTY_GUID,
        pageSize: state.pageSize,
        pageNumber: state.currentPage,
      }),
    placeholderData: keepPreviousData,
  });

  const updateCampaignId = async (campaignId: string | null) => {
    setState((prevState) => ({
      ...prevState,
      searchPayload: null,
      campaignId: campaignId,
    }));
  };

  const updateOrganizationId = async (organizationId: string | null) => {
    setState((prevState) => ({
      ...prevState,
      organizationId,
    }));
  };

  const updateSearchPayload = async (values: SearchValue | null) => {
    if (values && Object.keys(values).length) {
      setState((prevState) => ({
        ...prevState,
        searchPayload: values,
        currentPage: 1,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        searchPayload: null,
        currentPage: 1,
      }));
    }
  };

  const updateCurrentPage = async (currentPage: number) => {
    setState((prevState) => ({
      ...prevState,
      currentPage: currentPage,
    }));
  };

  const updateCurrentPageSize = async (pageSize: number) => {
    setState((prevState) => ({
      ...prevState,
      pageSize: pageSize,
    }));
  };

  const contextValue: CampaignContributionReportContextData = {
    id: state.campaignId,
    setId: updateCampaignId,
    campaigns: campaignLookupData.data,
    contributorTypes: reportLookups.data?.contributorTypes,
    methodOfGivingTypes: reportLookups.data?.methodOfGivingTypes,
    setOrganizationId: updateOrganizationId,
    searchValue: state.searchPayload ?? undefined,
    setSearchValue: updateSearchPayload,
    setCurrentPage: updateCurrentPage,
    setCurrentPageSize: updateCurrentPageSize,
    paging: {
      currentPage: state.currentPage,
      totalSize: campaignGridQuery?.data?.totalSize ?? 0,
    },
    pageSize: state.pageSize,
    refetchGrid: () => campaignGridQuery.refetch(),
    data: undefined,
  };

  if (campaignLookupData.isLoading)
    return <LoadingIndicator />;

  return (
    <CampaignContributionsReportContext.Provider value={contextValue}>
      <CampaignContributionReport data={campaignGridQuery?.data?.result} />
    </CampaignContributionsReportContext.Provider>
  );
};

export default CampaignsReportsContainer;
